import { useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, ChevronRight, Mail, FileText } from "lucide-react";
import { LEGAL_PAGES, LEGAL_FOOTNOTE, COMPANY } from "@/lib/legal-content";
import LegalFooter from "@/components/LegalFooter";
import PublicHeader from "@/components/PublicHeader";
import Logo from "@/components/Logo";

/**
 * Single thin route component that renders ANY of the 15 legal/policy pages
 * by reading slug from URL params and looking up content in LEGAL_PAGES.
 * Used by route /legal/:slug in App.js — keeps boilerplate per page near zero.
 */
export default function LegalPage() {
  const { slug } = useParams();
  const page = LEGAL_PAGES[slug];

  useEffect(() => {
    if (page) document.title = `${page.title} · TeeBee AI`;
  }, [page]);

  if (!page) return <Navigate to="/legal" replace />;

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid={`legal-page-${slug}`}>
      <PublicHeader />
      <div className="border-b border-border bg-background/80 backdrop-blur">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 py-3 flex items-center justify-end">
          <Link to="/legal" className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1.5">
            <ArrowLeft className="h-3.5 w-3.5" /> All policies
          </Link>
        </div>
      </div>

      <article className="max-w-5xl mx-auto px-5 sm:px-8 py-10 sm:py-14 grid grid-cols-1 lg:grid-cols-12 gap-10">
        {/* Sidebar TOC */}
        <aside className="hidden lg:block lg:col-span-3">
          <div className="sticky top-24">
            <p className="text-[10px] uppercase tracking-[0.25em] font-bold text-muted-foreground mb-3">On this page</p>
            <nav className="space-y-1.5">
              {page.sections.map((s) => (
                <a
                  key={s.id}
                  href={`#${s.id}`}
                  className="block text-xs text-muted-foreground hover:text-foreground transition-colors leading-snug"
                  data-testid={`legal-toc-${s.id}`}
                >
                  {s.title}
                </a>
              ))}
            </nav>
          </div>
        </aside>

        <div className="lg:col-span-9">
          <p className="text-[10px] uppercase tracking-[0.25em] font-bold text-violet-700 dark:text-violet-300">
            {page.eyebrow}
          </p>
          <h1 className="mt-3 font-display text-3xl sm:text-4xl tracking-tight font-medium leading-tight">
            {page.title}
          </h1>
          <p className="mt-4 text-[15px] text-muted-foreground leading-relaxed max-w-3xl">
            {page.intro}
          </p>
          <p className="mt-3 text-[11px] text-muted-foreground inline-flex items-center gap-3">
            <span>Last updated: <span className="font-medium text-foreground">{page.last_updated}</span></span>
            <span aria-hidden>·</span>
            <span>Version <span className="font-medium text-foreground">{page.version}</span></span>
          </p>

          <div className="mt-3 rounded-xl border border-amber-300/50 dark:border-amber-500/30 bg-amber-50/60 dark:bg-amber-500/10 p-3 text-[11px] text-amber-900 dark:text-amber-200" data-testid="legal-draft-note">
            <strong>Draft notice:</strong> Product-ready draft. Final wording is subject to legal review before production launch.
          </div>

          <div className="mt-10 space-y-10">
            {page.sections.map((s) => (
              <section key={s.id} id={s.id} className="scroll-mt-24" data-testid={`legal-section-${s.id}`}>
                <h2 className="font-display text-xl sm:text-2xl font-medium tracking-tight">{s.title}</h2>
                <div className="mt-4 space-y-3 text-[15px] leading-relaxed text-foreground/85">
                  {s.body.map((para, i) => (
                    <p key={`${s.id}-p-${i}`}>{para}</p>
                  ))}
                </div>
              </section>
            ))}
          </div>

          {/* Contact box */}
          <div className="mt-14 rounded-2xl border border-border bg-card p-6">
            <p className="text-[10px] uppercase tracking-[0.25em] font-bold text-muted-foreground">Questions about this policy?</p>
            <div className="mt-3 grid sm:grid-cols-2 gap-3 text-sm">
              <a href={`mailto:${COMPANY.emails.privacy}`} className="inline-flex items-center gap-2 hover:underline">
                <Mail className="h-3.5 w-3.5" /> {COMPANY.emails.privacy}
              </a>
              <a href={`mailto:${COMPANY.emails.support}`} className="inline-flex items-center gap-2 hover:underline">
                <Mail className="h-3.5 w-3.5" /> {COMPANY.emails.support}
              </a>
            </div>
            <p className="mt-4 text-xs text-muted-foreground">{LEGAL_FOOTNOTE}</p>
          </div>
        </div>
      </article>

      <LegalFooter />
    </div>
  );
}

/**
 * The /legal trust hub — links to all policy pages.
 */
export function LegalCenter() {
  const order = [
    "terms", "privacy", "refund-policy", "shipping-delivery", "pricing-billing",
    "coins-policy", "marketplace-policy", "profile-visibility-policy",
    "recruiter-policy", "ai-disclaimer", "content-policy",
    "community-guidelines", "student-data-policy", "cookie-policy", "contact",
  ];

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="legal-center">
      <PublicHeader />

      <main className="max-w-5xl mx-auto px-5 sm:px-8 py-12 sm:py-16">
        <p className="text-[10px] uppercase tracking-[0.25em] font-bold text-violet-700 dark:text-violet-300">
          Legal & Trust Center
        </p>
        <h1 className="mt-3 font-display text-3xl sm:text-5xl tracking-tight font-medium leading-tight">
          Built educator-first.<br/>
          <span className="text-muted-foreground">Documented honestly.</span>
        </h1>
        <p className="mt-5 text-muted-foreground max-w-2xl text-[15px] leading-relaxed">
          {LEGAL_FOOTNOTE} Every TeeBee policy is published in plain English with versioning and last-updated dates. Final wording is subject to legal review before production launch.
        </p>

        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {order.map((slug) => {
            const p = LEGAL_PAGES[slug];
            if (!p) return null;
            return (
              <Link
                key={slug}
                to={`/legal/${slug}`}
                className="group rounded-2xl border border-border bg-card p-4 hover:border-violet-400/60 hover:bg-violet-50/40 dark:hover:bg-violet-500/5 transition-colors"
                data-testid={`legal-card-${slug}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.22em] font-semibold text-muted-foreground">{p.eyebrow}</p>
                    <h3 className="mt-1.5 font-display text-base font-medium leading-snug">{p.title}</h3>
                  </div>
                  <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-violet-600 group-hover:translate-x-0.5 transition-all shrink-0" />
                </div>
                <p className="mt-3 text-[11px] text-muted-foreground">v{p.version} · {p.last_updated}</p>
              </Link>
            );
          })}
        </div>

        <div className="mt-12 rounded-2xl border border-border bg-card p-6">
          <p className="text-[10px] uppercase tracking-[0.25em] font-bold text-muted-foreground inline-flex items-center gap-2">
            <FileText className="h-3 w-3" /> Company identity
          </p>
          <div className="mt-3 grid sm:grid-cols-2 gap-4 text-sm">
            <div>
              <p className="font-medium">{COMPANY.legal_name}</p>
              <p className="mt-1 text-muted-foreground leading-relaxed">{COMPANY.address}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Support · <a href={`mailto:${COMPANY.emails.support}`} className="text-foreground hover:underline">{COMPANY.emails.support}</a></p>
              <p className="text-muted-foreground">Privacy · <a href={`mailto:${COMPANY.emails.privacy}`} className="text-foreground hover:underline">{COMPANY.emails.privacy}</a></p>
              <p className="text-muted-foreground">Billing · <a href={`mailto:${COMPANY.emails.billing}`} className="text-foreground hover:underline">{COMPANY.emails.billing}</a></p>
              <p className="mt-2 text-[11px] text-muted-foreground">{COMPANY.recognitions.join(" · ")}</p>
            </div>
          </div>
        </div>
      </main>

      <LegalFooter />
    </div>
  );
}
